import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { FormattedPlateResponse } from 'src/app/models/formattedPlateResponse';
import { FirstIssueState, PlateDetail } from 'src/app/models/plateDetail';
import { SoldPlate, SaleType } from 'src/app/models/soldPlate';
import { PlateOwner } from 'src/app/models/enums/plateOwner';
import { DVLAVehicleEnquiryResult } from 'src/app/models/dvlaVehicleEnquiryResult';
import { Registration } from 'src/app/models/registration';
import { RegistrationService } from 'src/app/services/registration-service';
import { ImageService } from 'src/app/services/image-service';
import { UserService } from 'src/app/services/user-service';
import { QuickSignupService } from 'src/app/services/quick-signup-service';
import { SessionService } from 'src/app/services/session-service';
import { TrackPlateService } from 'src/app/services/track-plate.service';
import { HeroGallery } from './components/hero-gallery/hero-gallery.component';
import { HttpClient } from '@angular/common/http';
import { ArchiveAdvert, TimelineEvent } from './components/timeline/timeline.component';
import {
  FirstRelease,
  firstReleaseOf,
  isUnreleased,
  predatesRelease,
  seriesPeriodOf,
  seriesYearOf,
} from '../../utils/plate-history';
import { PlateCarAssociation, plateCarAssociation } from '../../utils/plate-car-association';
import { normaliseMake } from 'src/app/consts/viewer-car-meta';
import { PlateKnowledgeReading, PlateKnowledgeService } from 'src/app/services/plate-knowledge.service';
import { PhotoAddComponent } from './components/photo-add/photo-add.component';
import { environment } from 'src/environments/environment';

// Historic advert clippings on the timeline: on every host, approved for
// launch by the owner on 2026-11-03 (site-config).
const ARCHIVE_ADVERTS_ENABLED = archiveAdvertsEnabled;
import {
  ClassifiedRegistration,
  classifyRegistration,
  datelessReferenceLink,
} from 'src/app/components/classification-pill/format-token';
import {
  PresentationSource,
  resolvePresentationAsset,
  resolveHeroAsset,
} from 'src/app/utils/presentation-asset';
import { AuctionLotDto, AuctionTab } from 'src/app/models/auction-lot';
import { AUCTIONS_DEV_ONLY, AuctionLotsService } from 'src/app/services/auction-lots-service';
import { PlatoPageFactsService } from 'src/app/services/plato-page-facts.service';
import { fmtLeft, gbp } from '../live-auctions/auction-format';
import { archiveAdvertsEnabled, showUnfinished, site } from 'src/app/site-config';

export type PlateDetailKind = 'new-issue' | 'resale';

// The hero value area is status-driven. Commercial states show a price; the
// remaining states surface a contextual action (track availability / sell).
export type HeroValueMode =
  | 'new-issue'
  | 'resale'
  | 'on-vehicle'
  | 'on-retention'
  | 'unknown';

export interface HeroFact {
  label: string;
  primary: string;
  secondary?: string;
}

// Editorial status/action panel that replaces the old CTA block. The hero first
// answers "what is the current state of this registration?" (heading + body +
// supporting), then "what can I do next?" (text-link actions). `handler` maps to
// an existing flow only — no new infrastructure is introduced here.
export type HeroActionHandler = 'sell' | 'track' | 'compare' | 'buy';

export interface HeroAction {
  label: string;
  handler: HeroActionHandler | null;
  emphasis: 'primary' | 'secondary' | 'button';
  /** External link (auction lot page) — rendered as <a target=_blank>. */
  href?: string;
}

/** One compact auction line shown directly under a dealer asking price. */
export interface HeroAuctionLine {
  text: string;
  linkLabel: string | null;
  href: string | null;
  tone: 'live' | 'closing' | 'soon' | 'result';
}

export interface HeroStatusPanel {
  heading: string;
  body: string;
  // 'price' renders the large display body; 'statement' renders a smaller,
  // subheading-weight line that reads as a sentence, not a second page title.
  bodyTone: 'price' | 'statement';
  supporting: string | null;
  actions: HeroAction[];
  /** 'alert' = red heading (auction closing within the hour). */
  headingTone?: 'alert' | 'auction';
  auctionLine?: HeroAuctionLine | null;
  /**
   * A second block with the same weight as the first (DEV-only): a dealer-listed
   * plate that is ALSO at auction shows "Listed for sale" then "At auction now".
   */
  secondary?: HeroStatusPanel | null;
  /** Placeholder while the status inputs are still loading (renders a skeleton). */
  loading?: boolean;
}

// Compact summary chips shown beneath the subheading. `kind` drives styling
// only. Every pill is either read from a real PlateX field (format) or produced
// by a deterministic rule over the registration characters (arrangement,
// numeric) — never inferred. Interpretation pills that would require
// word/name/acronym/initials classification data are intentionally NOT emitted
// because no such field is available on the plate-detail data.
export type HeroPillKind = 'format' | 'arrangement' | 'interpretation';

export interface HeroPill {
  /** Plain-English definition announced to assistive technology. */
  describe?: string;
  /** Optional deep-link target (format pills route into the Dateless Reference). */
  link?: any[];
  label: string;
  kind: HeroPillKind;
}

type PlateFormatKey = 'dateless' | 'prefix' | 'suffix' | 'current' | 'ni';

// DVLA's own offer for a new-release plate that is not in the DB yet (27 series,
// before the catalogue import). Read from the per-tag asset that
// analysis/new_release_27/build_dvla_tags.py builds from DVLA's snapshot.
export interface DvlaPresale {
  status: 'available' | 'auction' | 'unavailable';
  price: number | null;
  opensAt: string;
}

const DVLA_PRESALE_SERIES = '27';

// Longest the hero render waits for the current-car lookups before it falls
// back to the presentation asset.
const HERO_WAIT_MS = 3000;

interface RegHistoryEntry {
  reg_mark: string;
  vehicle_reg: string;
  make: string;
  model: string;
  colour: string;
  first_used_date: string;
  first_seen: string;
  last_seen: string;
  // Set only on grouped rows (see buildTimeline). A vehicle's rows are merged
  // into one card, and these carry the two facts the merge would otherwise
  // destroy: when the LAST pairing began, and whether the EARLIEST pairing was
  // filed under the mark or under the car's own registration.
  handover_first_seen?: string;
  origin_self_paired?: boolean;
}

// Shared plate detail page shell (dev2 redesign). Hero / pricing area. Data,
// plate rendering and assets are reused from the existing PlateX pipeline
// (the same fields the production plate-detail-page already displays); only the
// visual styling is new (from the "Plate Detail - PlateX 33.3" prototype).
@Component({
  selector: 'pla-plate-detail-v2',
  templateUrl: './plate-detail-v2.component.html',
  styleUrls: ['./plate-detail-v2.component.scss'],
})
export class PlateDetailV2Component implements OnInit, OnDestroy {
  public registrationString: string = null;
  public searchedTerm: string = null;
  public formattedRegistration: string = null;

  // Vehicle history (row-data reg_history) is withheld on release hosts until
  // a genuinely read-only endpoint exists: the current one also triggers DVLA
  // and DVSA lookups. dev2 and local dev keep it (site-config).
  public isLiveSite: boolean = !site.vehicleHistory;

  public detail: {
    plateDetail: PlateDetail;
    lastUpdate: Date;
    loaded: boolean;
  } = { plateDetail: undefined, lastUpdate: new Date(), loaded: false };

  // Vehicle assignment evidence (same sources as plate-detail-page):
  //   getVehicleInfo -> DVLA enquiry (non-null = currently on a vehicle)
  //   getRowData     -> reg_history (latest entry is the current vehicle)
  public vehicleInfo: DVLAVehicleEnquiryResult = null;
  public isRegisteredToVehicle: boolean = false;
  public regHistory: RegHistoryEntry[] = null;

  // Verified sale / auction records (same source as the production
  // sold-data-table / price-history: /solddata/history). Feeds the SALE rows of
  // the Plate History timeline. Listing/price-marker rows are filtered out.
  public soldHistory: SoldPlate[] = null;

  // Hero gallery image model — final production shape
  // ({ full, detailMain, detailTile, renderedPlate }). `full`, `detailMain` and
  // `detailTile` are all produced from a SINGLE plate render via
  // ImageService.quickBuildWithCrops, so the zoom tiles are true crops of the
  // exact vehicle/plate shown in the main image. `renderedPlate` is left empty
  // so the gallery uses the live <pla-plate> render.
  public heroGallery: HeroGallery = {
    full: '',
    detailMain: '',
    detailTile: '',
    renderedPlate: '',
  };

  // Presentation-continuity contract (see utils/presentation-asset.ts). When the
  // user arrives from a surface that pinned a presentation asset (e.g. a Search
  // Results Perfect Plate), these carry the asset identity + source so the hero
  // reuses the SAME imagery instead of independently choosing one. `pas` also
  // lets the source be labelled accurately (editorial / generated / owner).
  public presentationAssetId: string | null = null;
  public presentationSource: PresentationSource | null = null;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private registrationService: RegistrationService,
    private imageService: ImageService,
    private userService: UserService,
    private signupService: QuickSignupService,
    private sessionService: SessionService,
    private auctionLots: AuctionLotsService,
    private zone: NgZone,
    private pageFacts: PlatoPageFactsService,
    private http: HttpClient,
    private trackPlateService: TrackPlateService,
    private knowledge: PlateKnowledgeService
  ) {
    this.trackPlateService.refresh(false);
    this.registrationString = this.activatedRoute.snapshot.params.registration;
    this.searchedTerm =
      this.activatedRoute.snapshot.params.term ||
      this.activatedRoute.snapshot.queryParams['s'] ||
      null;
    // Presentation-continuity params passed by the originating surface.
    this.presentationAssetId =
      this.activatedRoute.snapshot.queryParams['pa'] || null;
    this.presentationSource =
      (this.activatedRoute.snapshot.queryParams['pas'] as PresentationSource) ||
      null;
    if (!this.registrationString) {
      this.router.navigate(['/landing']);
      return;
    }
    this.registrationString = this.registrationString
      .toUpperCase()
      .replace(' ', '');
  }

  // Credited photos of this plate on a vehicle (Plate Image Researcher).
  // Static asset for now (regenerate-the-asset pattern, like the market data);
  // a later increment reads approved rows from the editorial media layer.
  public platePhotos: any[] = null;

  // Historic adverts of this plate (archive clippings), newest first. Static
  // asset built by platex-valuation-lab analysis/archive_adverts_mass (export_ui.py).
  public archiveAdverts: ArchiveAdvert[] = [];

  // Memoisation for timelineEvents (see that getter): cached result + the input
  // identities it was built from, so scroll-time change detection reuses it.
  private _tlCache: TimelineEvent[] | null = null;
  private _tlKey: any[] | null = null;

  // Memoisation for knownCars: it is bound as an @Input to the add-photo form
  // and iterated with *ngFor, so returning a fresh array every change-detection
  // pass rebuilt the <option> elements constantly — which stopped the mobile
  // native <select> from committing a choice. Cache on the regHistory identity.
  private _knownCarsCache: {
    label: string;
    make: string;
    model: string;
    colour: string;
    year: string;
  }[] | null = null;
  private _knownCarsKey: any = null;

  ngOnInit(): void {
    this.getDetail();
    this.formatInput();
    // Confirmed knowledge readings can name the car a plate refers to (930 TBO
    // = Porsche 930 Turbo); the hero waits for them like the vehicle lookups.
    this.knowledge.get(this.registrationString).then((kn) => {
      this.knowledgeReadings = (kn && kn.readings) || [];
      this.knowledgeChecked = true;
      this.maybeRenderHero();
    });
    this.http.get<{ [reg: string]: any[] }>('assets/plate-photos/index.json').subscribe(
      (map) => {
        this.platePhotos = (map && map[this.registrationString]) || [];
      },
      () => {
        this.platePhotos = [];
      }
    );
    if (ARCHIVE_ADVERTS_ENABLED) {
      // Index is sharded by the first two characters of the mark.
      const shard = (this.registrationString || '').slice(0, 2);
      this.http
        .get<{ [reg: string]: ArchiveAdvert[] }>(`assets/archive-adverts/idx/${shard}.json`)
        .subscribe(
          (idx) => (this.archiveAdverts = (idx && idx[this.registrationString]) || []),
          () => (this.archiveAdverts = [])
        );
    }
    // Warm the watchlist so the Track action can reflect already-tracked state.
    if (this.sessionService.isLoggedIn()) {
      this.userService.getUserNotifies(() => {});
    }
    this.initAuction();
    this.loadDvlaPresale();
  }

  // ================= DVLA new-release offer (27 pre-sale) =================
  public dvlaPresale: DvlaPresale | null = null;

  private loadDvlaPresale(): void {
    const m = new RegExp(`^([A-Z]{2})${DVLA_PRESALE_SERIES}([A-Z]{3})$`).exec(
      this.registrationString || ''
    );
    if (!m) return;
    const [, tag, suffix] = m;
    this.http
      .get<any>(`assets/data/new-release/${DVLA_PRESALE_SERIES}-dvla/${tag}.json`)
      .subscribe(
        (d) => {
          if (!d) return;
          const auction = (d.auction || []).includes(suffix);
          const off = d.off === '*' || ` ${d.off} `.includes(` ${suffix} `);
          this.dvlaPresale = {
            status: auction ? 'auction' : off ? 'unavailable' : 'available',
            price: auction || off ? null : (d.p && d.p[suffix]) || d.base,
            opensAt: d.opensAt,
          };
        },
        () => (this.dvlaPresale = null)
      );
  }

  // Only while the DB has nothing better: once the catalogue import gives the
  // plate a DVLA owner, the normal new-issue path takes over.
  private get dvlaPresaleActive(): boolean {
    return !!this.dvlaPresale && this.kind !== 'new-issue' && !this.isOnVehicle;
  }

  private get dvlaOnSale(): boolean {
    const t = Date.parse(this.dvlaPresale?.opensAt || '');
    return !isNaN(t) && Date.now() >= t;
  }

  // DVLA's own page for the plate: before the sale it says when it can be
  // bought (or that it is held for auction); from 10am it is the buy page.
  public get dvlaUrl(): string {
    const enc = encodeURIComponent(this.registrationString);
    return `https://dvlaregistrations.dvla.gov.uk/search/results.html?search=${enc}&action=results&searched=true&super=${enc}`;
  }

  // "10am, Wednesday 4 November"
  private get dvlaSaleDay(): string {
    const d = new Date(this.dvlaPresale?.opensAt || '');
    if (isNaN(d.getTime())) return '';
    const day = d.toLocaleDateString('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    const time = d
      .toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: 'numeric', hour12: true })
      .replace(/\s/g, '');
    return `${time}, ${day.replace(',', '')}`;
  }

  /** The DVLA row the dealer marketplace shows for a pre-sale plate. */
  public get dvlaOffer(): { price: number; href: string; note: string; cta: string } | null {
    const d = this.dvlaPresale;
    if (!this.dvlaPresaleActive || d.status !== 'available' || d.price == null) return null;
    const open = this.dvlaOnSale;
    return {
      price: d.price,
      href: this.dvlaUrl,
      note: open ? 'New issue from DVLA' : `On sale from ${this.dvlaSaleDay}`,
      cta: open ? 'Buy from DVLA' : 'View on DVLA',
    };
  }

  private dvlaPanel(): HeroStatusPanel {
    const d = this.dvlaPresale;
    const track = { label: this.trackLabel, handler: 'track' as HeroActionHandler, emphasis: 'secondary' as const };
    if (d.status === 'available' && d.price != null) {
      const open = this.dvlaOnSale;
      return {
        heading: open ? 'Available now' : 'New release',
        body: `From £${this.formatMoney(d.price)}`,
        bodyTone: 'price',
        supporting: open
          ? 'New issue from DVLA'
          : `New issue from DVLA. On sale from ${this.dvlaSaleDay}.`,
        actions: [
          { label: open ? 'Buy from DVLA' : 'View on DVLA', handler: null, href: this.dvlaUrl, emphasis: 'button' },
          track,
        ],
      };
    }
    if (d.status === 'auction') {
      return {
        heading: 'Heading to auction',
        headingTone: 'auction',
        body: 'Held back by DVLA',
        bodyTone: 'statement',
        supporting: 'DVLA is keeping this plate for a future auction, so it is not in the general sale.',
        actions: [
          { label: 'View on DVLA', handler: null, href: this.dvlaUrl, emphasis: 'primary' },
          track,
        ],
      };
    }
    return {
      heading: 'Market Status',
      body: 'Not offered by DVLA',
      bodyTone: 'statement',
      supporting: 'DVLA is not selling this plate in the new release.',
      actions: [{ label: this.trackLabel, handler: 'track', emphasis: 'primary' }],
    };
  }

  ngOnDestroy(): void {
    clearInterval(this.auctionTickHandle);
    clearInterval(this.auctionPollHandle);
    clearTimeout(this.statusTimer);
  }

  // ================= Live / coming-soon auction (DEV-only) =================
  // Auction state is MARKET AVAILABILITY layered alongside the 4-status
  // ownership taxonomy — it never changes currentStatus. Looked up from the
  // existing /api/Auction/lots endpoint (q is a substring match, so filter on
  // the exact registrationKey). Prefer live over coming soon; a result from the
  // last 7 days shows as one quiet line. Fails silent (no auction UI).

  /** Live or coming-soon lot for this registration (null = none). */
  public auctionLot: AuctionLotDto | null = null;
  /** Ended lot (closed within 7 days, with a result) when no live/soon lot. */
  public auctionResult: AuctionLotDto | null = null;
  private auctionNow = Date.now();
  private auctionLeft = '';
  private auctionTickHandle: any;
  private auctionPollHandle: any;

  private initAuction(): void {
    if (!AUCTIONS_DEV_ONLY || !this.compactReg) return;
    this.loadAuction();
    setTimeout(() => (this.marketReported = true), 10000);
    this.zone.runOutsideAngular(() => {
      // Countdown: only re-enter Angular when the visible text changes
      // (per second under 1h, per minute/hour otherwise).
      this.auctionTickHandle = setInterval(() => {
        if (!this.auctionLot) return;
        this.auctionNow = Date.now();
        const left = this.computeAuctionLeft();
        if (left !== this.auctionLeft) this.zone.run(() => (this.auctionLeft = left));
      }, 1000);
      // Light refresh of the bid while the tab is visible.
      this.auctionPollHandle = setInterval(() => {
        if (document.visibilityState === 'visible' && this.auctionLot) {
          this.zone.run(() => this.loadAuction());
        }
      }, 60000);
    });
  }

  private loadAuction(): void {
    const key = this.compactReg;
    const find = (tab: AuctionTab) =>
      this.auctionLots.getLots({ q: key, tab, page: 1, pageSize: 10 }).pipe(
        map((r) => (r?.items || []).find((l) => (l.registrationKey || '').toUpperCase() === key) || null),
        catchError(() => of(null))
      );
    forkJoin([find('live'), find('soon'), find('results')]).subscribe(([live, soon, ended]) => {
      this.auctionLot = live || soon || null;
      const weekAgo = Date.now() - 7 * 864e5;
      const closed = ended?.biddingClosesAt ? Date.parse(ended.biddingClosesAt) : 0;
      const result = (ended?.resultStatus || '').toLowerCase();
      this.auctionResult =
        !this.auctionLot && ended && closed >= weekAgo && (result === 'sold' || result === 'not_sold')
          ? ended
          : null;
      this.auctionNow = Date.now();
      this.auctionLeft = this.computeAuctionLeft();
    });
  }

  private computeAuctionLeft(): string {
    const l = this.auctionLot;
    if (!l || !l.biddingClosesAt) return '';
    return fmtLeft(Date.parse(l.biddingClosesAt) - this.auctionNow);
  }

  private get auctionPhase(): 'live' | 'closing' | 'soon' | 'ended' | null {
    const l = this.auctionLot;
    if (!l) return null;
    const opens = l.biddingOpensAt ? Date.parse(l.biddingOpensAt) : 0;
    const closes = l.biddingClosesAt ? Date.parse(l.biddingClosesAt) : 0;
    if (l.status === 'soon' && (!opens || this.auctionNow < opens)) return 'soon';
    if (closes && this.auctionNow >= closes) return 'ended';
    if (closes && closes - this.auctionNow < 36e5) return 'closing';
    return 'live';
  }

  private auctionDay(iso: string | null): string {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }

  private auctionTime(iso: string | null): string {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' });
  }

  private auctionBidsText(l: AuctionLotDto): string | null {
    if (l.bidCount == null) return 'bids not published';
    // Zero bids: say so, unless the body itself already reads "No bids yet".
    if (l.bidCount === 0) return l.currentBidPence == null && l.startingPricePence == null ? null : 'no bids yet';
    const plus = l.bidCountLowerBound ? '+' : '';
    return `${l.bidCount}${plus} bid${l.bidCount === 1 && !plus ? '' : 's'}`;
  }

  private auctionReserveText(l: AuctionLotDto): string {
    switch (l.reserveStatus) {
      case 'met':
        return 'reserve met';
      case 'not_met':
        return 'reserve not met';
      case 'no_reserve':
        return 'no reserve';
      default:
        return 'reserve not published';
    }
  }

  /** Big price-slot figure: current bid, else starting price, else "No bids yet". */
  private auctionPriceBody(l: AuctionLotDto): { body: string; tone: 'price' | 'statement' } {
    if (l.currentBidPence != null) return { body: gbp(l.currentBidPence), tone: 'price' };
    if (l.startingPricePence != null) return { body: `Starting price ${gbp(l.startingPricePence)}`, tone: 'price' };
    return { body: 'No bids yet', tone: 'statement' };
  }

  /** Rule 1: plate only at auction — the auction occupies the price slot. */
  private auctionPanel(): HeroStatusPanel | null {
    const l = this.auctionLot;
    const phase = this.auctionPhase;
    if (!l || !phase || phase === 'ended') return null;
    if (phase === 'soon') {
      const opens = this.auctionDay(l.biddingOpensAt);
      const start = l.startingPricePence != null ? gbp(l.startingPricePence) : null;
      return {
        heading: 'Coming to auction',
        headingTone: 'auction',
        body: start ? `Starting price ${start}` : opens ? `Bidding opens ${opens}` : 'Coming soon',
        bodyTone: start ? 'price' : 'statement',
        supporting: [
          l.houseName,
          opens
            ? start
              ? `bidding opens ${opens}, ${this.auctionTime(l.biddingOpensAt)}`
              : `opens at ${this.auctionTime(l.biddingOpensAt)}`
            : null,
        ]
          .filter(Boolean)
          .join(' · '),
        actions: [
          { label: `View on ${l.houseName}`, handler: null, href: l.lotUrl, emphasis: 'primary' },
          { label: 'Remind me', handler: 'track', emphasis: 'secondary' },
        ],
      };
    }
    const price = this.auctionPriceBody(l);
    return {
      heading: phase === 'closing' ? 'Closing soon' : 'At auction now',
      headingTone: phase === 'closing' ? 'alert' : 'auction',
      body: price.body,
      bodyTone: price.tone,
      supporting: [
        l.houseName,
        this.auctionBidsText(l),
        this.auctionReserveText(l),
        l.allInPence != null ? `≈ ${gbp(l.allInPence)} all-in` : null,
        this.auctionLeft ? `ends in ${this.auctionLeft}` : null,
      ]
        .filter(Boolean)
        .join(' · '),
      actions: [
        { label: 'Bid now', handler: null, href: l.lotUrl, emphasis: 'button' },
        { label: 'Notify me', handler: 'track', emphasis: 'secondary' },
      ],
    };
  }

  /**
   * Rule 2 (dealer-listed AND at auction / coming to auction): an auction block
   * with the same structure and weight as the "Listed for sale" block above it —
   * heading, price, one supporting line (what the price is · house · when), and a
   * Bid now button.
   */
  private auctionBlock(): HeroStatusPanel | null {
    const l = this.auctionLot;
    const phase = this.auctionPhase;
    if (!l || !phase || phase === 'ended') return null;
    if (phase === 'soon') {
      const opens = this.auctionDay(l.biddingOpensAt);
      const start = l.startingPricePence != null ? gbp(l.startingPricePence) : null;
      return {
        heading: 'Coming to auction',
        headingTone: 'auction',
        body: start || (opens ? `Opens ${opens}` : 'Coming soon'),
        bodyTone: start ? 'price' : 'statement',
        supporting: [start ? 'Starting price' : null, l.houseName,
          opens ? `bidding opens ${opens}, ${this.auctionTime(l.biddingOpensAt)}` : null].filter(Boolean).join(' · '),
        actions: [{ label: 'View lot', handler: null, href: l.lotUrl, emphasis: 'button' }],
      };
    }
    const hasBid = l.currentBidPence != null;
    const amount = hasBid ? l.currentBidPence : l.startingPricePence;
    return {
      heading: phase === 'closing' ? 'Closing soon' : 'At auction now',
      headingTone: phase === 'closing' ? 'alert' : 'auction',
      body: amount != null ? gbp(amount) : 'No bids yet',
      bodyTone: amount != null ? 'price' : 'statement',
      supporting: [
        hasBid ? 'Current bid' : amount != null ? 'Starting price' : null,
        l.houseName,
        this.auctionLeft ? `ends in ${this.auctionLeft}` : null,
      ].filter(Boolean).join(' · '),
      actions: [{ label: 'Bid now', handler: null, href: l.lotUrl, emphasis: 'button' }],
    };
  }

  /** Rule 2/3: dealer-listed plate — one compact auction line under the price. */
  private auctionLineFor(): HeroAuctionLine | null {
    const l = this.auctionLot;
    const phase = this.auctionPhase;
    if (l && phase === 'soon') {
      const start = l.startingPricePence != null ? `starting price ${gbp(l.startingPricePence)}` : null;
      const opens = this.auctionDay(l.biddingOpensAt);
      return {
        text: ['Coming to auction', l.houseName, opens ? `bidding opens ${opens}` : null, start].filter(Boolean).join(' · '),
        linkLabel: 'View',
        href: l.lotUrl,
        tone: 'soon',
      };
    }
    if (l && (phase === 'live' || phase === 'closing')) {
      const bid =
        l.currentBidPence != null
          ? `current bid ${gbp(l.currentBidPence)}`
          : l.startingPricePence != null
          ? `starting price ${gbp(l.startingPricePence)}`
          : 'no bids yet';
      return {
        text: ['At auction now', l.houseName, bid, this.auctionLeft ? `ends in ${this.auctionLeft}` : null]
          .filter(Boolean)
          .join(' · '),
        linkLabel: 'Bid',
        href: l.lotUrl,
        tone: phase,
      };
    }
    return this.auctionResultLine();
  }

  /** Rule 4: ended within 7 days with a result — one quiet line. */
  private auctionResultLine(): HeroAuctionLine | null {
    const r = this.auctionResult;
    if (!r) return null;
    const price = r.hammerPence ?? r.currentBidPence;
    if (price == null) return null;
    const date = new Date(r.biddingClosesAt as string).toLocaleDateString('en-GB', {
      timeZone: 'Europe/London',
      day: 'numeric',
      month: 'short',
    });
    const sold = (r.resultStatus || '').toLowerCase() === 'sold';
    return {
      text: sold ? `Sold at ${r.houseName} auction on ${date} for ${gbp(price)}` : `Closed at ${gbp(price)}, not sold`,
      linkLabel: null,
      href: null,
      tone: 'result',
    };
  }

  private getDetail(): void {
    this.vehicleChecked = false;
    this.historyChecked = false;
    this.statusTimedOut = false;
    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => (this.statusTimedOut = true), 20000);
    this.registrationService
      .getPlateDetail(this.registrationString)
      .subscribe((d) => {
        // Release hosts never show a first-release date: the API derives it
        // from DVLA sale records, and a release date must not be inferred
        // from sale history. The page shows the series period instead.
        if (d && !showUnfinished) d.firstIssue = undefined;
        this.detail = { ...this.detail, plateDetail: d, loaded: true };
        this.fetchVehicleAndHistory();
      });
  }

  // Pull the same vehicle + sale-history fields the production plate-detail-page
  // already shows, so the hero status/price can be driven by real data.
  private fetchVehicleAndHistory(): void {
    const reg = this.registrationString.replace(' ', '');

    this.registrationService.getVehicleInfo(reg).subscribe(
      (v: DVLAVehicleEnquiryResult) => {
        this.vehicleInfo = v;
        this.isRegisteredToVehicle = v != null;
        this.vehicleChecked = true;
        this.maybeRenderHero();
      },
      () => {
        this.vehicleInfo = null;
        this.isRegisteredToVehicle = false;
        this.vehicleChecked = true;
        this.maybeRenderHero();
      }
    );

    // Release sites read the stored history only (reg-history: no DVLA/DVSA lookup, no writes);
    // dev2 keeps row-data.
    const history$: Observable<{ reg_history: RegHistoryEntry[] }> = this.isLiveSite
      ? this.registrationService.getRegHistory(reg)
      : this.registrationService.getRowData(reg);
    history$.subscribe(
      (data) => {
        this.regHistory =
          data?.reg_history?.length > 0 ? data.reg_history : null;
        this.historyChecked = true;
        this.maybeRenderHero();
      },
      () => {
        this.regHistory = null;
        this.historyChecked = true;
        this.maybeRenderHero();
      }
    );

    // Verified sale / auction history for the Plate History timeline (same
    // endpoint the production sold-data-table uses). Works on both live and dev2.
    this.registrationService.getPlateHistory(
      reg,
      (data: SoldPlate[]) => {
        this.soldHistory = data && data.length > 0 ? data : null;
      },
      () => {
        this.soldHistory = null;
      }
    );
  }

  private formatInput(): void {
    this.registrationService
      .FormatRegistration(this.registrationString)
      .subscribe((resp: FormattedPlateResponse) => {
        if (resp && resp.formattedRegistration) {
          // The API marks a series that is not issued yet (27 before 1 March)
          // invalid and returns it unspaced; space it locally instead.
          this.formattedRegistration =
            resp.valid === false
              ? this.registrationService.LocalFormatRegistration(this.registrationString)
              : resp.formattedRegistration;
          // Wait briefly for the current-car lookups so the hero is rendered
          // once on the matching vehicle; never longer than HERO_WAIT_MS.
          clearTimeout(this.heroWaitTimer);
          this.heroWaitTimer = setTimeout(() => this.maybeRenderHero(true), HERO_WAIT_MS);
          this.maybeRenderHero();
        }
      });
  }

  // Build the full vehicle render plus its two zoom crops from a single render
  // via the existing ImageService pipeline.
  //
  // Presentation continuity: this component does NOT independently pick a hero.
  // When a presentation asset was pinned by the originating surface (`pa` query
  // param), we reuse that exact curated vehicle so the hero matches the car the
  // user saw in Search Results. Absent a pinned asset (e.g. a direct visit), we
  // keep the existing default config. Fallback is deterministic: an unresolvable
  // asset returns a null config and we use the default.
  private heroRendered = false;
  private heroWaitTimer: any = null;
  private knowledgeReadings: PlateKnowledgeReading[] = [];
  private knowledgeChecked = false;

  private maybeRenderHero(force = false): void {
    if (this.heroRendered || !this.formattedRegistration) return;
    if (!force && !(this.vehicleChecked && this.historyChecked && this.knowledgeChecked)) return;
    this.heroRendered = true;
    clearTimeout(this.heroWaitTimer);
    this.generateFullRender();
  }

  // Reg history grouped one card per vehicle, plus which card (if any) the
  // plate is fitted to now. Shared by the timeline and the hero car so both
  // always name the same current vehicle.
  private groupedRegHistory(): {
    regs: RegHistoryEntry[];
    currentIdx: number;
    newest: RegHistoryEntry | null;
    stillFitted: boolean;
  } {
    // GROUP BY VEHICLE, NOT BY ROW. mot_reg_history records one row per
    // (plate, vehicle-registration) pairing, so a single car yields TWO rows
    // when the plate comes off it: one under the mark, one under the car's own
    // registration. HUG 11N's SLC appeared twice for exactly this reason, and
    // the second copy was being labelled "currently assigned". Rows describing
    // the same car — same make, model, colour and first-used date — collapse
    // into one card spanning min(first_seen) to max(last_seen).
    const rawRegs = this.regHistory || [];
    const byVehicle = new Map<string, RegHistoryEntry[]>();
    rawRegs.forEach((h) => {
      const key = [h.make, h.model, h.colour, h.first_used_date]
        .map((x) => (x || '').toString().trim().toUpperCase())
        .join('|');
      const list = byVehicle.get(key);
      if (list) list.push(h);
      else byVehicle.set(key, [h]);
    });
    const regs: RegHistoryEntry[] = Array.from(byVehicle.values()).map((rows) => {
      const sorted = rows
        .slice()
        .sort((a, b) => (a.first_seen || '').localeCompare(b.first_seen || ''));
      const firstSeen = sorted[0].first_seen;
      const lastSeen = sorted
        .map((r) => r.last_seen)
        .filter(Boolean)
        .sort()
        .pop();
      // Carry the LATEST row's vehicle_reg, not the on-mark one. Preferring the
      // on-mark row looked tidier but discarded the only evidence that the plate
      // had come off: for HUG 11N that meant merging away the FX66RUH row, so
      // the grouped card still claimed vehicle_reg == mark and the plate read as
      // fitted. The last pairing observed is what says where the plate is now.
      const latest = sorted[sorted.length - 1];
      return {
        ...latest,
        first_seen: firstSeen,
        last_seen: lastSeen,
        // When the plate came off, THIS is the date — the moment the car was
        // first seen back under its own registration. The group's first_seen is
        // the EARLIEST sighting of the car, so using it dated HUG 11N's
        // retention to August 2022 while the card below it read "2022 – 2025".
        handover_first_seen: latest.first_seen,
        // Whether the earliest row was filed under the mark itself. See
        // observedFrom below: a self-paired row carries the vehicle's whole MOT
        // history, not the plate's time on it.
        origin_self_paired: this.sameMark(sorted[0].vehicle_reg, sorted[0].reg_mark),
      };
    });

    // IS THE PLATE ON A CAR RIGHT NOW? Not "which row is newest" — that marked
    // HUG 11N as on a vehicle when it had gone onto retention, dating it from
    // the very timestamp it came OFF. The tell is vehicle_reg: the plate is on
    // a car only while the newest observation carries the mark itself. Once a
    // later row appears under the car's original registration, the plate has
    // been removed.
    const byRecency = regs
      .slice()
      .sort(
        (a, b) =>
          (this.yearOf(a.last_seen) || this.yearOf(a.first_seen) || 0) -
          (this.yearOf(b.last_seen) || this.yearOf(b.first_seen) || 0)
      );
    const newest = byRecency.length ? byRecency[byRecency.length - 1] : null;
    const stillFitted =
      !!newest && this.sameMark(newest.vehicle_reg, newest.reg_mark);
    const currentIdx = stillFitted ? regs.indexOf(newest) : -1;
    return { regs, currentIdx, newest, stillFitted };
  }

  // The PlateViewer hero follows one order on every plate:
  //   1. a STRONG plate-to-car association (F80 FER = Ferrari F80), from the
  //      mark itself or an owner-confirmed knowledge reading; weak readings
  //      never count (a lone 911 is not a Porsche)
  //   2. the most recent RELIABLY observed vehicle (see historyCar)
  //   3. otherwise the catalogue pick (pinned search asset or deterministic)
  // The render illustrates the plate; it never claims to show a real car.
  // Uploaded photos are never the hero.
  public get carAssociation(): PlateCarAssociation | null {
    return plateCarAssociation(this.registrationString, this.knowledgeReadings);
  }

  // The most recent vehicle the plate is reliably recorded on: the car it is
  // on now, exactly as the timeline shows it ("Present"), else the newest
  // earlier car. A car whose sightings all predate the plate's recorded first
  // release is not reliable (the mark was in DVLA stock) and is skipped. The
  // MOT pairings are preferred over the DVLA enquiry, whose make can be stale
  // (D 88 still read Porsche while fitted to a Range Rover); the DVLA make is
  // used only when there is no reg history (live site).
  private get historyCar(): { make: string; model: string; year: number | null } | null {
    if (this.regHistory && this.regHistory.length) {
      const { regs, currentIdx } = this.groupedRegHistory();
      const release = this.firstRelease;
      const reliable = (h: RegHistoryEntry) =>
        !!h.make && !predatesRelease(h.last_seen || h.first_seen, release);
      const latest = (h: RegHistoryEntry) => h.last_seen || h.first_seen || '';
      const cur =
        currentIdx >= 0 && reliable(regs[currentIdx])
          ? regs[currentIdx]
          : regs.filter(reliable).sort((a, b) => latest(b).localeCompare(latest(a)))[0];
      return cur
        ? { make: cur.make, model: cur.model || '', year: this.yearOf(cur.first_used_date) }
        : null;
    }
    const dvlaMake = (this.vehicleInfo?.make || '').trim();
    return dvlaMake
      ? { make: dvlaMake, model: '', year: this.yearOf(this.vehicleInfo?.monthOfFirstRegistration) }
      : null;
  }

  private generateFullRender(): void {
    if (!this.formattedRegistration) return;
    this.imageService.INIT();
    // The car the visitor clicked in Search (valid ?pa=) first, then
    // association, then vehicle history, then the deterministic default
    // (resolveHeroAsset). Picking a car in the viewer overrides all of these.
    // A make-only association (LAM 80 = Lamborghini) gives way to the plate's
    // own car of that make, which is more specific (its Aventador).
    const assoc = this.carAssociation;
    const history = this.historyCar;
    const car =
      assoc && !assoc.model && history && normaliseMake(history.make) === assoc.make
        ? history
        : assoc || history;
    const asset = car || this.presentationAssetId
      ? resolveHeroAsset(this.registrationString, car, {
          assetId: this.presentationAssetId,
          source: this.presentationSource,
        })
      : resolvePresentationAsset(this.registrationString);
    const config = asset && asset.config
      ? asset.config
      : this.imageService.getQuickConfig();
    this.imageService.quickBuildWithCrops(
      config,
      this.formattedRegistration,
      (r) => {
        this.heroGallery = {
          ...this.heroGallery,
          full: r.full,
          detailMain: r.detailMain,
          detailTile: r.detailTile,
        };
      }
    );
  }

  // ---- state-driven view model (derived from existing data only) ----

  public get plateDetail(): PlateDetail {
    return this.detail.plateDetail;
  }

  // New Issue (DVLA-sourced) vs Resale, derived from the existing PlateOwner
  // field — drives the [data-kind] styling hook only.
  public get kind(): PlateDetailKind {
    const owner = this.plateDetail?.owner;
    switch (owner) {
      case PlateOwner.DVLA:
      case PlateOwner.DVLA_PENDING:
      case PlateOwner.CURRENT_DVLA_AUCTION:
      case PlateOwner.DVLA_UNSOLD:
        return 'new-issue';
      default:
        return 'resale';
    }
  }

  // The registration itself is the dominant title (rendered as text in the
  // existing plate font in the template — not a plate badge).
  public get heroTitle(): string {
    return (
      this.formattedRegistration ||
      this.registrationService.LocalFormatRegistration(this.registrationString || '')
    );
  }

  // Registration-type subheading in title case: "Dateless Registration",
  // "Prefix Registration", "Suffix Registration", "Current Style Registration".
  // Because it carries the format, the facts box must NOT repeat it.
  public get subheading(): string {
    const pd = this.plateDetail;
    if (!pd) return '';
    const raw = `${pd.style || ''} ${pd.plateCategory || ''}`.toLowerCase();
    let word = '';
    if (raw.includes('dateless')) word = 'Dateless';
    else if (raw.includes('prefix')) word = 'Prefix';
    else if (raw.includes('suffix')) word = 'Suffix';
    else if (raw.includes('current')) word = 'Current Style';
    else {
      const base = (pd.style || pd.plateCategory || '').trim();
      word = base ? this.titleCase(base) : '';
    }
    return word ? `${word} Registration` : 'Registration';
  }

  // Exposed for the "Similar registrations" section so it can label results
  // deterministically (e.g. "Same format" / "Similar dateless format"). Reuses
  // the same format key the hero pills derive — never guesses.
  public get currentFormatKey(): string | null {
    return this.plateFormatKey;
  }

  // Exposed for the Market Information section so it can structurally sub-route
  // a dateless plate to the right index series (mirrors the valuation routing).
  public get currentCompactRegistration(): string {
    return this.compactReg;
  }

  // ---- hero pills (deterministic, data-backed only) ----

  // The compact, no-space registration used for character-level rules.
  private get compactReg(): string {
    return (this.registrationString || '').replace(/\s+/g, '').toUpperCase();
  }

  // Primary format, read from the existing PlateX style/category fields only.
  // Returns null when the data does not name a known format (we never guess).
  private get plateFormatKey(): PlateFormatKey | null {
    const pd = this.plateDetail;
    if (!pd) return null;
    const cat = (pd.plateCategory || '').toLowerCase();
    const raw = `${pd.style || ''} ${cat}`.toLowerCase();
    if (cat.includes('northern_ireland') || /\bni\b/.test(raw)) return 'ni';
    if (raw.includes('dateless')) return 'dateless';
    if (raw.includes('prefix')) return 'prefix';
    if (raw.includes('suffix')) return 'suffix';
    if (raw.includes('current')) return 'current';
    return null;
  }

  private get formatPillLabel(): string | null {
    switch (this.plateFormatKey) {
      case 'dateless':
        return 'Dateless';
      case 'prefix':
        return 'Prefix';
      case 'suffix':
        return 'Suffix';
      case 'current':
        return 'Current style';
      case 'ni':
        return 'NI';
      default:
        return null;
    }
  }

  // The two character groups of a clean two-group plate, in display order
  // (null for single- or multi-group plates such as prefix/suffix/current/NI,
  // which are 3-group and therefore get no arrangement pill — this is why
  // PLA 73X shows only "Suffix").
  private get arrangementGroups(): string[] | null {
    const groups = this.compactReg.match(/[A-Z]+|[0-9]+/g);
    return groups && groups.length === 2 ? groups : null;
  }

  // Dateless FORMAT under the canonical `letters × numbers` convention:
  // letters counted first regardless of visible order (1 BJ -> 2×1, 72 K ->
  // 1×2, WTF 1 -> 3×1). Classification shared with the Dateless Reference;
  // permanent examples covered in format-token.spec.ts.
  private get datelessFormat(): ClassifiedRegistration | null {
    if (this.plateFormatKey !== 'dateless') return null;
    return classifyRegistration(this.compactReg);
  }

  // Numeric: deterministic rule only. A dateless plate whose entire letter
  // component is O / OO / OOO behaves as a numeric plate under O/0 equivalence.
  private get isNumericPlate(): boolean {
    if (this.plateFormatKey !== 'dateless') return false;
    const reg = this.compactReg;
    const letters = (reg.match(/[A-Z]/g) || []).join('');
    const hasNumber = /[0-9]/.test(reg);
    return hasNumber && /^O{1,3}$/.test(letters);
  }

  // Final ordered pill list. Format (real data) -> arrangement (deterministic)
  // -> numeric (deterministic). No word/name/acronym/initials/perfect pills are
  // produced: the backing classification data does not exist on this page.
  public get heroPills(): HeroPill[] {
    const pills: HeroPill[] = [];
    const fmt = this.formatPillLabel;
    // The series lives with the classification: "Prefix series, August 1988
    // to July 1989" (tooltip and screen readers), separate from the release.
    const series = this.seriesPeriod;
    if (fmt) {
      pills.push({
        label: fmt,
        kind: 'format',
        describe: series
          ? this.isDateless
            ? `Dateless series, ${series}`
            : `${fmt} series, ${series}`
          : undefined,
      });
    }
    // The technical L_NN arrangement token is no longer shown as a pill: the
    // format pill and the plain-English dateless format (1×2) say it better.
    const format = this.datelessFormat;
    if (format) {
      pills.push({
        label: format.display,
        kind: 'arrangement',
        describe: format.plainEnglish,
        // deep link into the Dateless Reference with this format selected
        // (Phase 8: the public route; the shared helper owns the contract)
        link: datelessReferenceLink(format.token),
      });
    }
    if (this.isNumericPlate) {
      pills.push({ label: 'Numeric', kind: 'interpretation' });
    }
    return pills;
  }

  // From-price anchor (current asking). platexPrice is in minor units.
  public get fromPrice(): number | null {
    const minor = this.plateDetail?.platexPrice;
    return minor != null && minor > 0 ? minor / 100 : null;
  }

  public get hasPrice(): boolean {
    return this.fromPrice != null;
  }

  // The hero value area is status-driven. A live commercial price always wins
  // (New Issue stock price, or a resale asking price); otherwise the current
  // status decides which contextual action is offered. Historic sale prices are
  // deliberately NOT surfaced here — they live in the timeline/history.
  //   new-issue    -> DVLA stock with a price ("£X" / "New Issue Registration")
  //   resale       -> current asking price ("From £X" / "Across verified sellers")
  //   on-vehicle   -> Track availability + optional sell link
  //   on-retention -> Sell this plate prompt
  //   unknown      -> Track availability
  public get heroValueMode(): HeroValueMode {
    if (this.currentStatus === 'New Issue' && this.hasPrice) return 'new-issue';
    if (this.hasPrice) return 'resale';
    switch (this.currentStatus) {
      case 'On Vehicle':
        return 'on-vehicle';
      case 'On Retention':
        return 'on-retention';
      default:
        return 'unknown';
    }
  }

  // Drives the dealer marketplace supporting copy only. New Issue plates use the
  // new-issue wording; everything else uses the resale wording.
  public get marketplaceKind(): 'new-issue' | 'resale' {
    return this.heroValueMode === 'new-issue' ? 'new-issue' : 'resale';
  }

  // Already tracked? Reads the shared tracked set (TrackPlateService), the
  // same one the hero action row and Platefolio use.
  public get isTracked(): boolean {
    return this.trackPlateService.isTracked(this.registrationString);
  }

  // One label for every Track action on the page (matches the hero row).
  private get trackLabel(): string {
    return this.isTracked ? 'Tracking' : 'Track plate';
  }

  // Track plate: the shared tracking system. Signed out, the sign-in box opens
  // and the track completes after sign-in.
  public trackPlate(): void {
    if (this.isTracked) return;
    this.trackPlateService.track(
      this.registrationString,
      this.formattedRegistration || this.registrationString
    );
  }

  // Sell this registration — routes to the existing valuation/listing flow.
  // No valuation page yet: send owners to the sell page.
  public sellPlate(): void {
    this.router.navigate(['/sell']);
  }

  // Show the existing listings for this registration (search results page).
  public viewListings(): void {
    this.router.navigate([
      '/search-results',
      this.registrationString.replace(' ', ''),
    ]);
  }

  // Live marketplace min/max, emitted by the dealer table once it has loaded.
  // Lets the hero show a real "Range £LOW – £HIGH" summary from the same data.
  public marketRange: { low: number; high: number; sellers?: number } | null = null;

  // "6 sellers" under the hero price: says why there is a range, at no cost.
  public get sellerCountLabel(): string | null {
    const n = this.marketRange?.sellers;
    if (!n) return null;
    return n === 1 ? '1 seller' : `${n} sellers`;
  }

  public onMarketRange(range: { low: number; high: number; sellers?: number } | null): void {
    this.marketRange = range;
    this.marketReported = true;
    this.marketplaceEmitted = true;
    // Share what the page shows with the Plato assistant (numbers only).
    this.pageFacts.setListing(
      this.registrationString,
      range ? { low: range.low, high: range.high, sellers: range.sellers || 0 } : null
    );
  }

  /**
   * True once the dealer marketplace has reported (it always emits, null when
   * there are no listings or on error). The auction only takes over the price
   * slot after this, so a dealer-listed plate never flashes a big auction bid
   * before its RANGE arrives. 10s safety fallback in initAuction.
   */
  private marketReported = false;

  // Status inputs that arrive asynchronously. Until all of them (plus the
  // marketplace) have reported, the hero shows a neutral placeholder instead of
  // guessing — otherwise it flashed "Current status unknown" -> "On retention"
  // -> the real state while a plate that is on a vehicle and dealer-listed
  // loaded. statusTimedOut is a 20s safety net so a stalled call can never
  // leave the skeleton up forever (dev2 API calls routinely take 8–15s, so a
  // shorter net fired first and let the panel guess).
  // Set only by the marketplace itself — unlike marketReported, which the
  // auction code also forces true after 10s, before the dealer table has
  // actually answered (that briefly showed "No active listing observed").
  private marketplaceEmitted = false;
  private vehicleChecked = false;
  private historyChecked = false;
  private statusTimedOut = false;
  private statusTimer: any = null;

  private get statusReady(): boolean {
    if (this.statusTimedOut) return true;
    return (
      !!this.detail?.loaded &&
      this.vehicleChecked &&
      this.historyChecked &&
      this.marketplaceEmitted
    );
  }

  // "Range £LOW – £HIGH" built from the real marketplace min/max (display-only
  // rounding to whole pounds). Null until the dealer table has reported.
  public get priceRangeBody(): string | null {
    const r = this.marketRange;
    if (!r) return null;
    return `£${this.formatMoney(r.low)} to £${this.formatMoney(r.high)}`;
  }

  // Editorial status/action panel. Each mode maps to fixed copy and a small set
  // of text-link actions; the "what is it / what next" framing replaces the old
  // single green CTA. The On Vehicle "Last observed" line reuses `vehicleSince`.
  public get heroStatusPanel(): HeroStatusPanel {
    // Auction layer (DEV-only; null when no lot). Rule 1: no dealer asking
    // price/listings -> the auction occupies the price slot. Rule 2: dealer
    // listed -> keep the asking price, add one compact auction line.
    if (this.auctionLot || this.auctionResult) {
      if (!this.onMarket && this.marketReported) {
        const panel = this.auctionPanel();
        if (panel) return panel;
      }
      const base = this.basePanel();
      const block = this.auctionBlock();
      if (block) return { ...base, secondary: block };
      return { ...base, auctionLine: this.auctionLineFor() };
    }
    return this.basePanel();
  }

  private basePanel(): HeroStatusPanel {
    if (!this.statusReady) {
      return {
        heading: 'Market Status',
        body: '\u00a0',
        bodyTone: 'statement',
        supporting: null,
        actions: [],
        loading: true,
      };
    }
    const price = this.fromPrice;
    const range = this.priceRangeBody;

    // 27 pre-sale: DVLA's own offer from the snapshot asset. Dealer listings,
    // if any, keep the price slot and DVLA follows as a second block.
    if (this.dvlaPresaleActive) {
      const dvla = this.dvlaPanel();
      if (!range) return dvla;
      return {
        heading: AUCTIONS_DEV_ONLY ? 'Listed for sale' : 'Range',
        body: range,
        bodyTone: 'price',
        supporting: this.sellerCountLabel,
        actions: [],
        secondary: dvla,
      };
    }

    // A plate can be on a vehicle / on retention / status-unknown (DVLA-wise)
    // yet still be actively listed for sale by dealers. When the marketplace
    // has live listings (range present), always surface the price — never claim
    // "No active listing observed" while real priced listings are shown below.
    if (
      range &&
      (this.heroValueMode === 'on-vehicle' ||
        this.heroValueMode === 'on-retention' ||
        this.heroValueMode === 'unknown')
    ) {
      return {
        heading: AUCTIONS_DEV_ONLY ? 'Listed for sale' : 'Range',
        body: range,
        bodyTone: 'price',
        supporting: this.sellerCountLabel,
        actions: [],
      };
    }

    switch (this.heroValueMode) {
      case 'new-issue':
        return {
          heading: range ? (AUCTIONS_DEV_ONLY ? 'Listed for sale' : 'Range') : 'Available now',
          body: range || `£${this.formatMoney(price)}`,
          bodyTone: 'price',
          supporting: this.sellerCountLabel,
          actions: [],
        };
      case 'resale':
        return {
          heading: range ? (AUCTIONS_DEV_ONLY ? 'Listed for sale' : 'Range') : 'Available now',
          body: range || `From £${this.formatMoney(price)}`,
          bodyTone: 'price',
          supporting: this.sellerCountLabel,
          actions: [],
        };
      case 'on-vehicle':
        return {
          heading: 'Market Status',
          body: 'No active listing observed.',
          bodyTone: 'statement',
          supporting: null,
          actions: [
            // "See what it's worth now" removed until valuations are live (4 Nov 2026).
            { label: this.trackLabel, handler: 'track', emphasis: 'primary' },
          ],
        };
      case 'on-retention':
        return {
          heading: 'Market Status',
          body: 'On retention',
          bodyTone: 'statement',
          supporting: 'Not currently assigned to a vehicle',
          actions: [{ label: this.trackLabel, handler: 'track', emphasis: 'primary' }],
        };
      default:
        return {
          heading: 'Market Status',
          body: 'Current status unknown',
          bodyTone: 'statement',
          supporting: 'No recent assignment information available',
          actions: [{ label: this.trackLabel, handler: 'track', emphasis: 'primary' }],
        };
    }
  }

  private formatMoney(value: number | null): string {
    return (value || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 });
  }

  public runHeroAction(handler: HeroActionHandler | null): void {
    switch (handler) {
      case 'sell':
        this.sellPlate();
        return;
      case 'track':
        this.trackPlate();
        return;
      case 'buy':
      case 'compare':
        this.viewListings();
        return;
    }
  }

  // Conditional intelligence line — only when the user searched a word.
  public get intelligenceLine(): string | null {
    const term = this.searchedTerm && this.searchedTerm.trim();
    return term ? term : null;
  }

  private get isDateless(): boolean {
    const pd = this.plateDetail;
    const s = `${pd?.style || ''} ${pd?.plateCategory || ''}`.toLowerCase();
    return s.includes('dateless');
  }

  // The plate is on a vehicle when DVLA returns a live enquiry result, or when
  // the LATEST observation still pairs the mark with a car.
  //
  // "has any history at all" was the old test, and it is wrong the moment a
  // plate comes off: HUG 11N carried three cars since 1974 and now sits on a
  // retention certificate, yet read "On Vehicle" purely because we hold its
  // past. History proves where a plate HAS been, never where it is.
  //
  // mot_reg_history pairs (plate, vehicle-registration). While the plate is
  // fitted the newest row carries the mark itself; once removed, a later row
  // appears under the car's own registration (HUG 11N -> FX66RUH, 2025-09-17).
  public get isOnVehicle(): boolean {
    if (this.isRegisteredToVehicle) return true;
    const rows = this.regHistory || [];
    if (!rows.length) return false;
    const newest = rows
      .slice()
      .sort((a, b) =>
        (a.first_seen || '').localeCompare(b.first_seen || '')
      )
      .pop();
    return !!newest && this.sameMark(newest.vehicle_reg, newest.reg_mark);
  }

  // Current status taxonomy (only these four values, no DVLA phrasing, no
  // ownership labels):
  //   On Vehicle   -> vehicle history shows a current assignment
  //   New Issue    -> genuine DVLA stock
  //   On Retention -> privately held but not on a vehicle
  //   Unknown      -> otherwise
  public get currentStatus(): string {
    if (this.isOnVehicle) return 'On Vehicle';
    const owner = this.plateDetail?.owner;
    switch (owner) {
      case PlateOwner.DVLA:
      case PlateOwner.DVLA_PENDING:
      case PlateOwner.CURRENT_DVLA_AUCTION:
      case PlateOwner.DVLA_UNSOLD:
        // A DVLA owner flag can be stale: 812 FER still carries "current DVLA
        // auction" from its 2018 DVLA sale, yet sold at Collecting Cars on
        // 30 Oct 2026. A later sale outside DVLA proves it left DVLA stock.
        if (!this.soldOutsideDvla) return 'New Issue';
        return this.vehicleChecked && !this.isRegisteredToVehicle ? 'On Retention' : 'Unknown';
      case PlateOwner.PRIVATE:
      case PlateOwner.PLATEX:
      case PlateOwner.RESELLER:
        return 'On Retention';
      default:
        if (this.dvlaPresale && this.dvlaPresale.status !== 'unavailable') {
          return 'New Issue';
        }
        // Deduction: the DVLA enquiry has answered and says the plate is not
        // on a vehicle, and we hold evidence it was genuinely issued (a sale
        // record or past vehicle history). A privately held mark that is off
        // a vehicle is on certificate — say "On Retention", not "Unknown"
        // (e.g. 812 FER: retention Mar 2026, owner field unset, yet we showed
        // "Current status unknown" while holding both sale records).
        if (
          this.vehicleChecked &&
          !this.isRegisteredToVehicle &&
          ((this.soldHistory || []).length > 0 ||
            (this.regHistory || []).length > 0)
        ) {
          return 'On Retention';
        }
        return 'Unknown';
    }
  }

  // True when the sold history holds a sale by anyone other than DVLA (an
  // auction house). DVLA sales are excluded because DVLA can re-sell a mark
  // that returned to its stock.
  private get soldOutsideDvla(): boolean {
    return (this.soldHistory || []).some(
      (s) =>
        s.saleType === SaleType.VERIFIED_AUCTION ||
        s.saleType === SaleType.THIRD_PARTY_AUCTION_IMPORT ||
        s.saleType === SaleType.PRIVATE_AUCTION_HOUSE
    );
  }

  // Short "since" line for an on-vehicle plate, e.g. "Feb 2025 – Present".
  public get vehicleSince(): string | null {
    const e = this.regHistory && this.regHistory[0];
    if (e && e.first_seen) {
      const d = new Date(e.first_seen);
      if (!isNaN(d.getTime())) {
        return `${d.toLocaleString('en-GB', {
          month: 'short',
          year: 'numeric',
        })} to present`;
      }
    }
    return null;
  }

  // Shorten an issuing-authority label for the hero facts box. Generic + safe:
  // drop explanatory parentheticals and keep the concise base name, optionally
  // suffixed with an abbreviation derived from a short directional/region hint
  // (e.g. "London (North-West) (used for ... before 1965)" -> "London NW").
  // The full authority string belongs in a later provenance/audit section.
  private shortenAuthority(raw: string): string {
    if (!raw) return '';
    const base = raw.split('(')[0].replace(/\s+/g, ' ').trim();
    let suffix = '';
    const paren = raw.match(/\(([^)]+)\)/);
    if (paren) {
      const inner = paren[1].trim();
      const banned = /\b(used|before|after|for|council|county|formerly|now)\b/i;
      if (inner.length <= 14 && !/\d/.test(inner) && !banned.test(inner)) {
        const initials = inner
          .split(/[\s\-/]+/)
          .filter(Boolean)
          .map((w) => w.charAt(0).toUpperCase())
          .join('');
        if (initials.length >= 2 && initials.length <= 4) suffix = ` ${initials}`;
      }
    }
    let out = `${base}${suffix}`.trim();
    if (!out) out = raw.trim();
    if (out.length > 22) out = base || out.slice(0, 22).trim();
    return out;
  }

  private titleCase(s: string): string {
    if (!s) return '';
    return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }

  // Stored brand logos shipped in assets/car-logos (Cardog optimised icon set).
  // Only these makes have a logo; everything else falls back to the generic
  // car glyph in the timeline.
  private static readonly VEHICLE_LOGO_SLUGS = new Set<string>([
    'acura', 'alfa-romeo', 'aston-martin', 'audi', 'bentley', 'bmw', 'bugatti',
    'buick', 'byd', 'cadillac', 'chevrolet', 'chrysler', 'dodge', 'ferrari',
    'fiat', 'ford', 'genesis', 'gmc', 'honda', 'hummer', 'hyundai', 'infiniti',
    'jaguar', 'jeep', 'kia', 'koenigsegg', 'lamborghini', 'landrover', 'lexus',
    'lincoln', 'lotus', 'lucid', 'maserati', 'mazda', 'mb', 'mclaren', 'mini',
    'mitsubishi', 'nissan', 'pagani', 'polestar', 'porsche', 'ram', 'rivian',
    'rolls-royce', 'subaru', 'tesla', 'toyota', 'vinfast', 'volkswagen', 'volvo',
  ]);

  // Map a raw DVLA make (e.g. "LAND ROVER", "MERCEDES-BENZ", "VW") to a stored
  // brand-logo asset path, or null when no logo exists for that make.
  private vehicleLogoFor(make: string | null | undefined): string | null {
    if (!make) return null;
    let slug = make.toLowerCase().trim().replace(/[\s_]+/g, '-');
    const aliases: { [k: string]: string } = {
      'land-rover': 'landrover',
      'range-rover': 'landrover',
      mercedes: 'mb',
      'mercedes-benz': 'mb',
      'mercedes-amg': 'mb',
      vw: 'volkswagen',
      'rolls-royce-motor-cars': 'rolls-royce',
      'alfa': 'alfa-romeo',
    };
    slug = aliases[slug] || slug;
    return PlateDetailV2Component.VEHICLE_LOGO_SLUGS.has(slug)
      ? `assets/car-logos/${slug}.svg`
      : null;
  }

  // Map a DVLA colour name (the standard registered-colour vocabulary) to a
  // representative swatch hex for the vehicle card's accent bar. Returns null
  // for unknown/unmapped colours so the card falls back to its neutral default.
  private static readonly COLOUR_HEX: { [k: string]: string } = {
    black: '#1B1B1B',
    white: '#DDDBD4',
    silver: '#C3C5C8',
    grey: '#9BA1A2',
    gray: '#9BA1A2',
    blue: '#22407A',
    red: '#9E2B25',
    green: '#1F5135',
    yellow: '#D9B23A',
    orange: '#C2622A',
    brown: '#5A3D2B',
    beige: '#C8B89A',
    bronze: '#8C6A43',
    gold: '#B08D3E',
    purple: '#4B2E63',
    pink: '#B5567E',
    maroon: '#5E2230',
    cream: '#E4DCC4',
    turquoise: '#2B8A8A',
  };

  private colourHexFor(colour: string | null | undefined): string | null {
    if (!colour) return null;
    const key = colour.toLowerCase().trim().split(/[\s/-]+/)[0];
    return PlateDetailV2Component.COLOUR_HEX[key] || null;
  }

  // Release history for the hero, from the shared rules in utils/plate-history
  // (the timeline uses the same ones):
  //   First released  only where recorded, with its method (DVLA auction or
  //                   DVLA fixed price)
  //   Series          the period the format implies; context, never presented
  //                   as this plate's issue date
  //   Plate Origin    the series' issuing office
  public get facts(): HeroFact[] {
    const pd = this.plateDetail;
    if (!pd) return [];
    const out: HeroFact[] = [];
    const release = this.firstRelease;
    if (release) {
      out.push({ label: 'First released', primary: release.label, secondary: release.method });
    } else if (isUnreleased(pd)) {
      out.push({ label: 'First released', primary: 'Not yet released', secondary: 'Held in DVLA stock' });
    }
    const series = this.seriesPeriod;
    if (series) {
      out.push({
        label: this.isDateless ? 'Series introduced' : 'Series',
        primary: series.replace(/^introduced /, ''),
      });
    }
    const issuedBy = this.shortenAuthority(pd.issueRegion || '');
    if (issuedBy) out.push({ label: 'Plate Origin', primary: issuedBy });
    return out;
  }

  // Identity line under the registration. A recorded release leads and stands
  // alone ("First released September 2012 · DVLA auction"); the series lives
  // in the format pill and the timeline. Without a recorded release the line
  // states the series as a series ("Series August 1996 to July 1997 ·
  // Manchester", "Series introduced 1903 · Kent"), never as an issue date.
  public get identityLine(): string | null {
    const f = this.facts;
    const release = f.find((x) => x.label === 'First released' && x.primary !== 'Not yet released');
    if (release) return `First released ${release.primary} · ${release.secondary}`;
    const parts: string[] = [];
    const series = f.find((x) => x.label === 'Series' || x.label === 'Series introduced');
    if (series) parts.push(`${series.label} ${series.primary}`);
    if (f.some((x) => x.primary === 'Not yet released')) parts.push('Not yet released');
    const origin = f.find((x) => x.label === 'Plate Origin');
    if (origin) parts.push(origin.primary);
    return parts.length ? parts.join(' · ') : null;
  }

  public get firstRelease(): FirstRelease | null {
    return firstReleaseOf(this.plateDetail);
  }

  // "August 1988 to July 1989", or "introduced 1903" for a dateless series.
  private get seriesPeriod(): string | null {
    return seriesPeriodOf(this.plateDetail, this.compactReg, this.isDateless);
  }

  // Future conditional market-insight line. Hidden until real, time-bound,
  // segment-level movement data exists.
  public get marketInsight(): string | null {
    return null;
  }

  // ------------------------------------------------------- Plate History timeline
  // The provenance / resale timeline is relevant for NON new-issue plates only
  // (genuine DVLA stock has no assignment / sale history to show). It is also
  // suppressed unless there are at least TWO substantive history elements
  // (e.g. first issue + an assignment or sale) — a lone "released" event isn't
  // a timeline, so the whole section is hidden rather than shown half-empty.
  // Cars from our records, to populate the "Add a photo" which-car selector.
  public get knownCars(): {
    label: string;
    make: string;
    model: string;
    colour: string;
    year: string;
  }[] {
    // Dedupe by VEHICLE exactly as the timeline does (make|model|colour|
    // first-used-date), so the "which car?" picker offers the SAME cars as the
    // timeline shows — not one option per raw mot_reg_history pairing. Each
    // option carries the year it appears under on the timeline, so the member
    // can orientate, and so a chosen car pins the photo to the right row rather
    // than always the current one.
    if (this._knownCarsCache && this._knownCarsKey === this.regHistory) {
      return this._knownCarsCache;
    }
    const rawRegs = this.regHistory || [];
    const byVehicle = new Map<string, RegHistoryEntry[]>();
    rawRegs.forEach((h) => {
      const key = [h.make, h.model, h.colour, h.first_used_date]
        .map((x) => (x || '').toString().trim().toUpperCase())
        .join('|');
      const list = byVehicle.get(key);
      if (list) list.push(h);
      else byVehicle.set(key, [h]);
    });
    const cars = Array.from(byVehicle.values()).map((rows) => {
      const sorted = rows
        .slice()
        .sort((a, b) => (a.first_seen || '').localeCompare(b.first_seen || ''));
      const latest = sorted[sorted.length - 1];
      const lastSeen = sorted
        .map((r) => r.last_seen)
        .filter(Boolean)
        .sort()
        .pop();
      const selfPaired = this.sameMark(sorted[0].vehicle_reg, sorted[0].reg_mark);
      const observedFrom = selfPaired ? null : this.yearOf(sorted[0].first_seen);
      const observedTo = this.yearOf(lastSeen);
      const startYear = observedFrom || observedTo || null;
      const make = this.titleCase((latest.make || '').toLowerCase());
      const model = this.titleCase((latest.model || '').toLowerCase());
      const name = [make, model].filter(Boolean).join(' ') || 'Vehicle';
      let when = '';
      if (observedFrom && observedTo && observedFrom !== observedTo)
        when = `${observedFrom} to ${observedTo}`;
      else if (observedFrom || observedTo) when = String(observedFrom || observedTo);
      return {
        label: when ? `${name} · ${when}` : name,
        make,
        model,
        colour: latest.colour ? this.titleCase(latest.colour.toLowerCase()) : '',
        year: startYear ? String(startYear) : '',
        _sort: startYear || 0,
      };
    });
    // Newest first, mirroring the timeline order (current/most recent at top).
    cars.sort((a, b) => b._sort - a._sort);
    const out = cars.map(({ _sort, ...c }) => c);
    this._knownCarsKey = this.regHistory;
    this._knownCarsCache = out;
    return out;
  }

  // A member just submitted a photo. Show it on the timeline immediately,
  // badged "Pending review" (pending: true) and visible only to them, so the
  // contribution is acknowledged rather than vanishing until an editor acts.
  // Member photo uploads need POST platedetails/{reg}/photo, which the production API doesn't have yet.
  public photoAddEnabled = showUnfinished;
  @ViewChild(PhotoAddComponent) private photoAdd?: PhotoAddComponent;

  // "+ Add a photo" on a timeline vehicle card: open the form for that car.
  public onAddPhoto(year: string): void {
    if (this.photoAdd) this.photoAdd.openFor(year);
  }

  public onPhotoAdded(p: {
    image: any; // string CDN url, or a sanitizer-trusted local SafeUrl
    make: string;
    model: string;
    colour: string;
    seen: string;
    credit: string;
    matchYear?: string; // the chosen known-car's timeline year, to pin correctly
  }): void {
    if (!this.platePhotos) this.platePhotos = [];
    this.platePhotos = [
      {
        match: {
          make: (p.model || p.make || '').replace(/\(.*?\)/g, '').trim(),
          year: p.matchYear || '',
        },
        image: p.image,
        car: [p.make, p.model].filter(Boolean).join(' ').trim() || 'Vehicle',
        colour: p.colour || '',
        seen: p.seen || '',
        credit: p.credit || 'a PlateX user',
        sourceUrl: '',
        licence: '',
        pending: true,
      },
      ...this.platePhotos,
    ];
  }

  public get showTimeline(): boolean {
    if (this.currentStatus === 'New Issue') return false;
    // Substantive history = at least two events, OR a credited photo (a photo
    // is itself substantive provenance, and a just-contributed one should never
    // be hidden behind an event-count threshold).
    return (
      this.historyEventCount >= 2 ||
      (this.platePhotos || []).length > 0 ||
      this.archiveAdverts.length > 0
    );
  }

  // Count of substantive provenance events: first issue (ORIGIN), vehicle
  // assignments and verified auction sales. The live NOW/market row is excluded
  // because it reflects current status, not history.
  private get historyEventCount(): number {
    return this.timelineEvents.filter(
      (e) => e.node === 'origin' || e.node === 'vehicle' || e.node === 'sale'
    ).length;
  }

  // The plate is "on the market" when it carries an asking price or the dealer
  // marketplace surfaced live listings. Drives the live (NOW) timeline row.
  public get onMarket(): boolean {
    return this.hasPrice || !!this.marketRange;
  }

  // Build the timeline view model from EXISTING PlateX data only:
  //   NOW              <- on-market state (price / live dealer listings)
  //   VEHICLE_ASSIGNMENT <- reg_history (latest entry = currently fitted)
  //   SALE             <- solddata/history verified sale & auction records
  //   ORIGIN           <- plate-detail issue month/year/region (first issue)
  // Vehicle + sale rows are merged and sorted newest-first; NOW is pinned to the
  // top and ORIGIN to the bottom. Nothing is invented.
  public get timelineEvents(): TimelineEvent[] {
    // Memoise: this getter is bound in the template ([events]="timelineEvents"),
    // so Angular calls it on EVERY change-detection pass — including the many
    // fired while scrolling. Rebuilding a fresh array each time replaced the
    // vehicle <img> elements and made the photo flicker on scroll. We recompute
    // only when one of the data inputs the timeline is built from actually
    // changes (identity compare — these arrays/objects are replaced wholesale on
    // load, and platePhotos is replaced on a new submission).
    const key: any[] = [
      this.onMarket,
      this.registrationString,
      this.formattedRegistration,
      this.plateDetail,
      this.regHistory,
      this.soldHistory,
      this.platePhotos,
      this.vehicleInfo,
      this.archiveAdverts,
    ];
    if (
      this._tlCache &&
      this._tlKey &&
      this._tlKey.length === key.length &&
      key.every((v, i) => v === this._tlKey[i])
    ) {
      return this._tlCache;
    }

    const events: TimelineEvent[] = [];

    // --- NOW (on the market) ---
    if (this.onMarket) {
      events.push({
        node: 'now',
        yearTag: 'NOW',
        yearTagNow: true,
        isCurrent: true,
        kind: 'On the market',
        chip: this.formattedRegistration || this.registrationString || '',
        nowText: 'Available to buy',
        nowSub: 'Listed in the live dealer market.',
      });
    }

    // --- dated rows: vehicle assignments + sales, newest first ---
    const dated: { sort: number; ev: TimelineEvent }[] = [];

    const { regs, currentIdx, newest, stillFitted } = this.groupedRegHistory();
    const release = this.firstRelease;

    // DVLA's V5C date goes on the card of the car DVLA names: the current car
    // when it matches, else the newest earlier car of that make the plate was
    // still seen on at that date. F80 FER: DVLA names the Ferrari (V5C
    // November 2012) while the latest MOT records name a BAC Mono.
    const v5c = this.vehicleInfo?.dateOfLastV5CIssued || null;
    let dvlaIdx = currentIdx >= 0 && this.vehicleMatchesDvla(regs[currentIdx].make) ? currentIdx : -1;
    if (dvlaIdx < 0 && v5c && !predatesRelease(v5c, release)) {
      regs.forEach((h, i) => {
        if (
          this.vehicleMatchesDvla(h.make) &&
          String(h.last_seen || '').slice(0, 7) >= String(v5c).slice(0, 7) &&
          (dvlaIdx < 0 || (h.last_seen || '') > (regs[dvlaIdx].last_seen || ''))
        ) {
          dvlaIdx = i;
        }
      });
    }

    regs.forEach((h, i) => {
      const isCurrent = i === currentIdx;
      // Three separate kinds of evidence, kept apart on the card:
      //   registered  — the vehicle's own DVLA first registration (vehicle fact)
      //   assigned    — V5C issue date (plate fact; current vehicle only)
      //   observed    — MOT sightings of the plate on this vehicle (plate fact)
      // Dates the data doesn't support are omitted, never inferred.
      // A row whose reg_mark EQUALS its vehicle_reg carries the vehicle's whole
      // MOT history re-attributed to the plate it wears today — not the plate's
      // time on it. Proof: across DVLA auction plates, which are never-issued by
      // definition and so cannot have been on any car before their sale, 372 of
      // 1,115 self-paired rows (33.4%) are "seen" before the sale date, against
      // 0 of 255 cross-paired rows. So a start date is only claimed when the
      // earliest pairing was filed under the car's OWN registration.
      //
      // CONFLICTS. A sighting earlier than the plate's recorded first release
      // cannot be the plate on the car: the mark was in DVLA stock. MOT records
      // label a car's older tests with the mark it carries later (F80 FER's
      // Ferrari 550 reads "2006" though DVLA auctioned the mark in September
      // 2012), so such a start date is withheld and the conflict stated.
      const beforeRelease = predatesRelease(h.first_seen, release);
      const allBeforeRelease = predatesRelease(h.last_seen || h.first_seen, release);
      const observedFrom =
        h.origin_self_paired || beforeRelease ? null : this.yearOf(h.first_seen);
      const observedTo = allBeforeRelease ? null : this.yearOf(h.last_seen);
      const registered = this.monthYearOf(h.first_used_date);
      const assigned = i === dvlaIdx ? this.assignedDateLabel : null;
      const notes: string[] = [];
      if (beforeRelease && !h.origin_self_paired) {
        notes.push(
          `MOT records also list this plate on this car from ${this.yearOf(h.first_seen)}, before DVLA first released it in ${release.label}. That earlier date is not treated as evidence.`
        );
      }
      if (isCurrent && i !== dvlaIdx && (this.vehicleInfo?.make || '').trim()) {
        notes.push(
          `The DVLA vehicle record names a ${this.titleCase(this.vehicleInfo.make.toLowerCase())}. The latest MOT records name this car.`
        );
      }
      const assignedYear = assigned
        ? this.yearOf(this.vehicleInfo?.dateOfLastV5CIssued)
        : null;

      let period: string;
      if (isCurrent) {
        period =
          !assigned && observedFrom
            ? `Observed ${observedFrom} to present`
            : 'Present';
      } else if (observedFrom && observedTo && observedFrom !== observedTo) {
        period = `Observed ${observedFrom} to ${observedTo}`;
      } else if (!observedFrom && observedTo) {
        // Start unknown (self-paired origin). "Observed 2025" would read as the
        // year it went ON, which is the opposite of what the data says.
        period = `Observed until ${observedTo}`;
      } else if (observedFrom || observedTo) {
        period = `Observed ${observedFrom || observedTo}`;
      } else {
        period = '';
      }
      const name = [h.make, h.model]
        .map((s) => this.titleCase((s || '').toLowerCase()))
        .filter(Boolean)
        .join(' ')
        .trim();
      // Timeline position = when the plate is evidenced ON the vehicle: the
      // assignment year when known, else the first observation year.
      const startYear = assignedYear || observedFrom || observedTo;
      dated.push({
        // Vehicles sort just above a sale of the same year (assignment precedes
        // the later sale visually within a year).
        sort: (startYear || 0) * 100 + 60,
        ev: {
          node: 'vehicle',
          yearTag: startYear ? String(startYear) : '',
          isCurrent,
          vehicleName: name || 'Vehicle',
          vehicleAssigned: assigned,
          vehicleRegistered: registered ? `Registered ${registered}` : null,
          vehicleColour: h.colour
            ? this.titleCase(h.colour.toLowerCase())
            : null,
          vehicleColourHex: this.colourHexFor(h.colour),
          vehiclePeriod: period,
          vehicleLogo: this.vehicleLogoFor(h.make),
          vehicleNote: notes.length ? notes.join(' ') : null,
        },
      });
    });

    // RETENTION. When the newest observation is under the car's OWN
    // registration rather than the mark, the plate came off that car — and the
    // timestamp of that first sighting is when. Both of Hugh's lookups and the
    // DVSA MOT service independently report HUG 11N as on no vehicle, and our
    // own data dates the handover to 2025-09-17. MOT sightings are periodic, so
    // this is when the change was OBSERVED, not necessarily the day it happened
    // — hence "Observed", matching the wording already used on vehicle cards.
    if (!stillFitted && newest && (newest.handover_first_seen || newest.first_seen)) {
      const offAt = newest.handover_first_seen || newest.first_seen;
      const offYear = this.yearOf(offAt);
      const offOn = this.monthYearOf(offAt);
      dated.push({
        // Sorts above the vehicle it came off, below a sale in the same year.
        sort: (offYear || 0) * 100 + 70,
        ev: {
          node: 'retention',
          yearTag: offYear ? String(offYear) : '',
          kind: 'Retention',
          saleTitle: 'Removed from vehicle',
          saleMeta: offOn ? `On retention · observed ${offOn}` : 'On retention',
        },
      });
    }

    // Fallback current assignment from the live DVLA enquiry. row-data
    // (reg_history) is sparse and is NOT fetched at all on the live site, but
    // getVehicleInfo IS — and a non-null result is exactly what drives the
    // "On Vehicle" hero status. So when reg_history yielded no vehicle rows yet
    // the plate is confirmed on a vehicle, build the current assignment from the
    // same DVLA data (make / colour). Nothing invented; this is the only vehicle
    // source available on production.
    if (regs.length === 0 && this.isRegisteredToVehicle && this.vehicleInfo) {
      const v = this.vehicleInfo;
      const name = this.titleCase((v.make || '').toLowerCase());
      // The V5C issue date gives the assignment month (and year tag / sort
      // position); without it the card reads "Currently assigned ... Present"
      // with no asserted date.
      const assignedYear = this.yearOf(v.dateOfLastV5CIssued);
      const registered = this.monthYearOf(v.monthOfFirstRegistration);
      dated.push({
        sort: (assignedYear || 0) * 100 + 60,
        ev: {
          node: 'vehicle',
          yearTag: assignedYear ? String(assignedYear) : '',
          isCurrent: true,
          vehicleName: name || 'Vehicle',
          vehicleAssigned: this.assignedDateLabel,
          vehicleRegistered: registered ? `Registered ${registered}` : null,
          vehicleColour: v.colour
            ? this.titleCase(v.colour.toLowerCase())
            : null,
          vehicleColourHex: this.colourHexFor(v.colour),
          vehiclePeriod: 'Present',
          vehicleLogo: this.vehicleLogoFor(v.make),
        },
      });
    }

    (this.soldHistory || [])
      .filter((s) => this.isTimelineSale(s))
      .forEach((s) => {
        const year = this.yearOf(s.sold_date as any);
        // The card used to carry only the year tag, so a user could never see
        // WHEN a sale happened beyond a four-digit number. DVLA publishes month
        // precision; the auction houses give a real day. monthYearOf() is the
        // honest common denominator and is already used for vehicle rows.
        const soldOn = this.monthYearOf(s.sold_date as any);
        const amount = this.saleAmount(s);
        const source = this.saleSource(s);
        const isAuction = this.isAuctionType(s.saleType);
        // DVLA only ever sells never-issued marks, so the DVLA sale IS the
        // moment this registration came into existence on the road. It carries
        // both roles rather than sitting under a separate invented 1981 node.
        const isFirstIssue = this.isIssuingSale(s);
        dated.push({
          sort: this.inYearSort(year, s.sold_date as any, 40),
          ev: {
            node: 'sale',
            gapTop: true,
            yearTag: year ? String(year) : '',
            kind: isFirstIssue ? 'First release · Sale' : 'Sale',
            saleTitle: isFirstIssue
              ? release?.method === 'DVLA fixed price'
                ? "First released by DVLA at its fixed price"
                : 'First released by DVLA at auction'
              : isAuction
              ? 'Sold at auction'
              : 'Sold',
            saleAmount: amount,
            saleMeta: [soldOn, source].filter(Boolean).join(' · ')
              ? `${amount ? ' · ' : ''}${[soldOn, source].filter(Boolean).join(' · ')}`
              : '',
          },
        });
      });

    // --- credited photos (Plate Image Researcher) ---
    // Attach a matched photo to its vehicle row; where the photo shows a car
    // reg_history does not have (common for exotic and veteran plates), add a
    // photo-sourced row shown as "Seen <year>". The car facts come from the
    // photo's own record, never invented, and the "Photo: <source>" credit
    // marks it.
    (this.platePhotos || []).forEach((p) => {
      const photo = {
        url: p.image,
        credit: p.credit,
        sourceUrl: p.sourceUrl,
        licence: p.licence,
        cropped: !!p.cropped,
        pending: !!p.pending,
      };
      if (p.match && p.match.make) {
        const want = String(p.match.make).toLowerCase();
        const wantYear = p.match.year ? String(p.match.year) : '';
        const candidates = dated.filter(
          (d) =>
            d.ev.node === 'vehicle' &&
            (d.ev.vehicleName || '').toLowerCase().includes(want)
        );
        // When the member picked a specific car (so we have its year), pin to the
        // vehicle row for THAT year; otherwise fall back to the first match. This
        // stops a photo of the earlier of two same-model cars landing on the
        // current one.
        const hit =
          (wantYear && candidates.find((d) => d.ev.yearTag === wantYear)) ||
          candidates[0];
        if (hit) {
          hit.ev.vehiclePhoto = photo;
          // A card that carries a photo states when it was SEEN (the sighting),
          // not the car's MOT-observed range — "Observed" stays for cards
          // without a photo, where it is the only evidence of the period.
          hit.ev.vehiclePeriod = p.seen ? `Seen ${p.seen}` : 'Seen';
          return;
        }
      }
      const yr = parseInt(p.seen, 10) || 0;
      const make = (p.car || '').split(' ')[0];
      dated.push({
        // A sighting is known to the year only, so it sits at the start of that
        // year, below anything with a real date. A plate seen on a car is on
        // the car, not on retention, and is sold at auction off retention: 812
        // FER was photographed on the Ferrari and later that year sold at
        // Collecting Cars, so the sighting must not read as after the sale.
        sort: yr * 100 + 1,
        ev: {
          node: 'vehicle',
          yearTag: p.seen ? String(p.seen) : '',
          isCurrent: false,
          vehicleName: p.car || 'Vehicle',
          vehicleColour: p.colour || null,
          vehicleColourHex: this.colourHexFor(p.colour),
          vehiclePeriod: p.seen ? `Seen ${p.seen}` : 'Seen',
          vehicleLogo: this.vehicleLogoFor(make),
          vehiclePhoto: photo,
        },
      });
    });

    // --- historic adverts (archive clippings) ---
    // An asking price at a dated publication, not a sale: its own node, sorted
    // by publication month within the year.
    // A mark cannot be advertised before DVLA first issued it, so an advert
    // dated earlier than the first issue is a misread or a wrong match and is
    // dropped rather than shown.
    const issued = this.firstIssueDate;
    this.distinctAdverts(
      (this.archiveAdverts || []).filter((ad) => !issued || (this.isoDayOf(ad.date) || '') >= issued)
    ).forEach((ad) => {
        const year = this.yearOf(ad.date as any);
        dated.push({
          sort: this.inYearSort(year, ad.date, 40),
          ev: {
            node: 'advert',
            gapTop: true,
            yearTag: year ? String(year) : '',
            advert: ad,
          },
        });
      });

    dated
      .sort((a, b) => b.sort - a.sort)
      .forEach((d) => events.push(d.ev));

    // --- ORIGIN: the SERIES the mark belongs to, not a first issue ---
    //
    // This node used to read "First issue / Issued in London NW / August 1981",
    // built entirely from the mark's letters: X denotes the period starting
    // August 1981 and PLA maps to that office. That is what the format implies,
    // never a record of issuance — and DVLA cannot sell a previously issued
    // mark, so for every plate it held back and later auctioned the claim was
    // simply false. PLA 73X was first issued on 19 March 2025, not in 1981.
    //
    // So the node now states the series and says plainly that the mark sat
    // unissued. It is rendered inert (greyed) because it is an inference from
    // the characters, not an event: nothing happened to this plate in 1981.
    //
    // Every plate now gets this series treatment, not only DVLA stock: the
    // format's period is never a record of when an individual mark was issued
    // (rule shared with the hero, utils/plate-history). The note says what is
    // known about the release, and says so when nothing is.
    const pd = this.plateDetail;
    const seriesPeriod = this.seriesPeriod;
    if (pd && seriesPeriod) {
      const region = this.shortenAuthority(pd.issueRegion || '');
      const seriesYear = seriesYearOf(pd, this.compactReg, this.isDateless);
      const note = release
        ? `Held in DVLA stock until first released in ${release.label}, ${
            release.method === 'DVLA auction' ? 'at DVLA auction' : "at DVLA's fixed price"
          }.`
        : isUnreleased(pd)
        ? 'Not yet released. Still held in DVLA stock.'
        : 'This is the series the mark belongs to. When this plate was first released is not recorded.';
      events.push({
        node: 'origin',
        gapTop: true,
        yearTag: seriesYear ? String(seriesYear) : '',
        kind: 'Series',
        inert: true,
        originLetter: this.originAreaCode,
        originTitle: this.originTitleFor(region, true),
        originSub: this.isDateless ? `Series ${seriesPeriod}` : seriesPeriod,
        originNote: note,
      });
    }

    this._tlKey = key;
    this._tlCache = events;
    return events;
  }

  // Origin title. Two forms, and which one is used is a claim about evidence.
  //
  //   series = false  "Issued in Leeds" — the original wording, kept for every
  //                   plate outside DVLA stock, where the issue date we hold is
  //                   the one derived from the plate data as it always was
  //   series = true   "Suffix series · London NW" — used only where DVLA is the
  //                   issuer, because there the mark demonstrably was NOT issued
  //                   in its series year and saying so would be false
  //
  // Central/national issue values are not place names ("centralised"), so the
  // region is dropped for those; genuine place names are kept (title-cased only
  // when the source value is all lowercase, so abbreviations like "London NW"
  // survive).
  private originTitleFor(region: string, series = false): string {
    if (!series) {
      if (!region) return 'First issued';
      if (/central|national|dvla/i.test(region)) return 'Centrally issued';
      const place =
        region === region.toLowerCase() ? this.titleCase(region) : region;
      return `Issued in ${place}`;
    }
    const style = (this.plateDetail?.style || '').trim();
    const label = style ? `${this.titleCase(style.toLowerCase())} series` : 'Series';
    if (!region || /central|national|dvla/i.test(region)) return label;
    const display =
      region === region.toLowerCase() ? this.titleCase(region) : region;
    return `${label} · ${display}`;
  }

  // Sales and adverts share one in-year position by date (month, then day),
  // so an advert and a sale in the same year read in true order. Rows without
  // a month keep their old fixed slot.
  private inYearSort(year: number | null, value: string | null | undefined, fallback: number): number {
    const d = this.isoDayOf(value);
    const month = d ? parseInt(d.slice(5, 7), 10) : 0;
    const day = d ? parseInt(d.slice(8, 10), 10) : 0;
    return (year || 0) * 100 + (month ? 20 + month + day / 100 : fallback);
  }

  // A mark is often re-advertised through a year at the same or a near price
  // (the same dealer advert in several titles). Within a calendar year, adverts
  // within 5% of the price that opened their run are one repeat group, and only
  // the clearest print of the group is shown (the export's clarity score; the
  // earliest on a tie), so the timeline shows real price moves, not repeats.
  // Prices are only compared on the same VAT basis.
  private static readonly ADVERT_PRICE_TOLERANCE = 0.05;

  private distinctAdverts(ads: ArchiveAdvert[]): ArchiveAdvert[] {
    const open = new Map<string, ArchiveAdvert[]>();
    const groups: ArchiveAdvert[][] = [];
    [...ads]
      .sort((a, b) => a.date.localeCompare(b.date))
      .forEach((ad) => {
        const key = `${this.yearOf(ad.date)}|${ad.vat}`;
        const run = open.get(key);
        if (
          run &&
          Math.abs(ad.price - run[0].price) <=
            PlateDetailV2Component.ADVERT_PRICE_TOLERANCE * run[0].price
        ) {
          run.push(ad);
          return;
        }
        const next = [ad];
        open.set(key, next);
        groups.push(next);
      });
    return groups.map((g) =>
      g.reduce((best, ad) => ((ad.clarity || 0) > (best.clarity || 0) ? ad : best))
    );
  }

  // YYYY-MM-DD for any date the API or the advert index gives, else null.
  private isoDayOf(value: string | null | undefined): string | null {
    if (!value) return null;
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return m[0];
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  // ISO date (YYYY-MM-DD) DVLA first issued the mark, when the API knows it.
  private get firstIssueDate(): string | null {
    const fi = this.plateDetail?.firstIssue;
    if (
      !fi ||
      (fi.state !== FirstIssueState.ISSUED_AT_AUCTION &&
        fi.state !== FirstIssueState.ISSUED_AT_FIXED_PRICE) ||
      !fi.date
    )
      return null;
    return this.isoDayOf(fi.date as any);
  }

  // Is this sale the one that FIRST ISSUED the mark? True only for the DVLA sale
  // the API identified, matched on date — DVLA cannot sell a previously issued
  // registration, so that sale is the plate's origin.
  private isIssuingSale(s: { sold_date?: any }): boolean {
    const fi = this.plateDetail?.firstIssue;
    if (
      !fi ||
      (fi.state !== FirstIssueState.ISSUED_AT_AUCTION &&
        fi.state !== FirstIssueState.ISSUED_AT_FIXED_PRICE) ||
      !fi.date
    )
      return false;
    const a = String(s?.sold_date || '').slice(0, 10);
    const b = String(fi.date).slice(0, 10);
    return !!a && a === b;
  }

  // Origin node mark = the AREA CODE for the registration's style.
  //   - Current style (2001+): the two-letter DVLA memory tag, i.e. the first
  //     two characters of the registration (e.g. "JA54CKY" -> "JA"). This is the
  //     regional identifier; the plate-detail issueRegion for these reads
  //     "centralised" (central DVLA issue) rather than a place name.
  //   - Dateless: the plate's own area-code letter group.
  //   - Fallback: the issuing-region initial, then a neutral glyph.
  private get originAreaCode(): string {
    const reg = (
      this.plateDetail?.registrationString ||
      this.registrationString ||
      ''
    )
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    const style = (
      this.plateDetail?.plateCategory ||
      this.plateDetail?.style ||
      ''
    ).toLowerCase();

    // Current style AA00AAA -> first two letters are the area code / memory tag.
    if (/current/.test(style) || /^[A-Z]{2}[0-9]{2}[A-Z]{3}$/.test(reg)) {
      const tag = reg.slice(0, 2);
      if (/^[A-Z]{2}$/.test(tag)) return tag;
    }

    // Dateless: the plate's own letter group reads as the area code.
    if (this.isDateless) {
      const groups = this.arrangementGroups;
      const letterGroup = groups && groups.find((g) => /[A-Z]/.test(g));
      if (letterGroup) return letterGroup.slice(0, 2).toUpperCase();
    }

    const region = (this.plateDetail?.issueRegion || '').trim();
    if (region) return region.charAt(0).toUpperCase();
    return '✦';
  }

  // Verified sales at auction, plus the DVLA fixed-price sale that first issued
  // the mark (a real DVLA record with DVLA's own date, e.g. HA73FUL May 2023
  // £699). We deliberately do NOT surface non-auction dealer "sold"/withdrawn
  // rows, listing markers (CURRENT_LISTING) or price markers
  // (INITIAL_PRICE/PRICE_CHANGE) on the timeline.
  private isTimelineSale(s: SoldPlate): boolean {
    if (this.isAuctionType(s.saleType)) return true;
    return s.saleType === SaleType.DVLA && this.isIssuingSale(s);
  }

  private isAuctionType(t: SaleType): boolean {
    return (
      t === SaleType.DVLA_AUTION ||
      t === SaleType.DVLA_AUCTION_IMPORT ||
      t === SaleType.THIRD_PARTY_AUCTION_IMPORT ||
      t === SaleType.VERIFIED_AUCTION ||
      t === SaleType.PRIVATE_AUCTION_HOUSE
    );
  }

  // Price is stored in minor units (the sold-data-table divides sold_price by
  // 100). Prefer sold_price; fall back to the numeric price field.
  private saleAmount(s: SoldPlate): string | null {
    let minor: number | null = null;
    if (s.sold_price != null && String(s.sold_price).trim() !== '') {
      const n = parseFloat(String(s.sold_price).replace(/[^0-9.]/g, ''));
      if (!isNaN(n) && n > 0) minor = n;
    }
    if (minor == null && s.price != null && s.price > 0) minor = s.price;
    if (minor == null) return null;
    return `£${this.formatMoney(minor / 100)}`;
  }

  // Actual source of the sale. Auction types and the DVLA fixed-price issue reach the timeline
  // (see isTimelineSale). DVLA auction rows always read "DVLA Auction" — their
  // seller field carries internal pipeline tags (e.g. "DVLA AUCTION (LIVE
  // PRICE SYNC)") that must never reach the UI. Third-party / verified
  // auctions show the cleaned seller name, falling back to "Auction".
  private saleSource(s: SoldPlate): string {
    switch (s.saleType) {
      case SaleType.DVLA_AUTION:
      case SaleType.DVLA_AUCTION_IMPORT:
        return 'DVLA Auction';
      case SaleType.DVLA:
        return 'DVLA';
      default:
        return this.cleanSellerLabel(s.seller) || 'Auction';
    }
  }

  // Seller strings arrive as raw import values: parenthetical pipeline
  // annotations, all-caps, acronyms and possessives that naive title-casing
  // mangles ("Rm Sotheby'S"). Strip the annotations, title-case, then restore
  // acronym casing and possessive 's.
  private cleanSellerLabel(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const stripped = raw
      .replace(/\([^)]*\)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!stripped) return null;
    return this.titleCase(stripped.toLowerCase())
      .replace(/(['’])S(\s|$)/g, '$1s$2')
      .replace(/\bDvla\b/g, 'DVLA')
      .replace(/\bRm\b/g, 'RM')
      .replace(/\bH&h\b/g, 'H&H')
      .replace(/^Cc Auction$/, 'Collecting Cars');
  }

  // "Assigned <Month Year>" for the current vehicle, from the DVLA V5C issue
  // date (dateOfLastV5CIssued): assigning a plate to a vehicle triggers a new
  // V5C, so its issue date is the best assignment evidence DVLA exposes. Month
  // precision only — a V5C reissue can postdate the assignment (keeper/address
  // changes), so a day-precise claim would overstate the evidence.
  private get assignedDateLabel(): string | null {
    const monthYear = this.monthYearOf(this.vehicleInfo?.dateOfLastV5CIssued);
    return monthYear ? `Assigned ${monthYear}` : null;
  }

  // "September 2016" from a date string ("2016-09-23") or year-month value
  // ("2016-09", DVLA monthOfFirstRegistration). Null when unparsable.
  private monthYearOf(value: string | null | undefined): string | null {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    const month = d.toLocaleString('en-GB', { month: 'long' });
    return `${month} ${d.getFullYear()}`;
  }

  // True when a reg_history make agrees with the DVLA enquiry's current
  // vehicle — the guard for attaching current-only facts (the V5C assignment
  // date) to a reg_history card, since reg_history can lag reality.
  private vehicleMatchesDvla(make: string | null | undefined): boolean {
    const dvlaMake = (this.vehicleInfo?.make || '').trim().toUpperCase();
    const rowMake = (make || '').trim().toUpperCase();
    return !!dvlaMake && !!rowMake && dvlaMake === rowMake;
  }

  // Extract a 4-digit year from a date string / Date-parsable value.
  // Two registration strings describing the same mark, ignoring spacing/case.
  // mot_reg_history stores reg_mark spaced ("HUG 11N") and vehicle_reg raw.
  private sameMark(a: string, b: string): boolean {
    const norm = (v: string) => (v || '').replace(/\s+/g, '').toUpperCase();
    const x = norm(a);
    return !!x && x === norm(b);
  }

  private yearOf(value: string | null | undefined): number | null {
    if (!value) return null;
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d.getFullYear();
    const m = String(value).match(/(19|20)\d{2}/);
    return m ? parseInt(m[0], 10) : null;
  }
}
